import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress, Tooltip,
    RadioGroup, FormControlLabel, Radio, Grid,
} from '@mui/material';
import { listProgramRequestsService, updateProgramRequestStatusService } from '../../../services/client.service';
import { getActiveChatInsiderProgramService, clearChatStateInsiderService, publishLearningProgramService, linkLearningProgramService } from '../../../services/learning_programs.service';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES, WORKSPACE_REQUEST_STATUS } from '../../../utils/constants';

const NAVY = 'var(--brand-text, #052F44)';
const ORANGE = '#FF6900';
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

// Slide 22 prints the rule beside the estimate: "30credit x # of session".
// Named rather than inlined so there is one place to change it if DQ ever
// makes the rate per-partner.
const CREDITS_PER_SESSION = 30;

// sessionStorage key — links the request the owner chose to build to the program
// they publish next, so delivery is tied to an ACTUAL published program (not a
// free button). Consumed + cleared in the program dashboard's publish handler.
const PENDING_REQUEST_KEY = 'dq_pending_program_request_id';

// Keyed by numeric status.
const STATUS_CHIP = {
    [WORKSPACE_REQUEST_STATUS.PENDING]:     { label: 'Pending',           bg: '#EFA020' },
    [WORKSPACE_REQUEST_STATUS.IN_PROGRESS]: { label: 'Building…',         bg: '#2563EB' },
    [WORKSPACE_REQUEST_STATUS.BUILT]:       { label: 'Program Delivered', bg: '#1B6B55' },
    [WORKSPACE_REQUEST_STATUS.DECLINED]:    { label: 'Declined',          bg: '#9CA3AF' },
};

// Decline reasons (updated slide 32). A reason is REQUIRED — the school always
// gets a why. The deck lists two presets + Other (the old "Assessment data does
// not support this area" option was removed in the updated deck).
const DECLINE_REASONS = [
    'Not enough license capacity right now',
    'Out of scope for this cycle',
];
const DECLINE_OTHER = 'Other';

const navBtnSx = {
    textTransform: 'none', fontSize: '12.5px', color: ORANGE, borderColor: ORANGE,
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
    '&.Mui-disabled': { color: ORANGE, borderColor: ORANGE, opacity: 0.4 },
};

// Orange primary action. `color` must be set explicitly: overriding
// backgroundColor in sx leaves the label on the theme's dark contrastText,
// which is why these buttons render with black text otherwise. Same fix the
// app's other orange buttons use (see PublishProgramDialogs).
const primaryBtnSx = {
    textTransform: 'none', whiteSpace: 'nowrap', backgroundColor: ORANGE, color: '#fff',
    '&:hover': { backgroundColor: '#e55e00' },
    '&.Mui-disabled': { backgroundColor: ORANGE, color: '#fff', opacity: 0.6 },
};

// Dialog action pair — pill buttons, matching the app's existing confirmation
// dialogs (PublishProgramDialogs): outlined secondary + orange primary.
const dialogPrimarySx = {
    background: ORANGE, color: '#fff', borderRadius: '50px', textTransform: 'none',
    fontWeight: 700, px: 3, boxShadow: 'none',
    '&:hover': { background: '#c9541e' },
    '&.Mui-disabled': { background: ORANGE, color: '#fff', opacity: 0.6 },
};
const dialogSecondarySx = {
    borderRadius: '50px', textTransform: 'none', fontWeight: 700, px: 3, lineHeight: 1.2,
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};
// Slide 294 renders this as "10 × 45 min".
const fmtDuration = (r) => {
    const s = r.num_sessions, m = r.session_duration_mins;
    if (!s && !m) return '—';
    return `${s || '?'} × ${m || '?'} min`;
};
const fmtGroup = (r) => [r.grade_level, r.class_name].filter(Boolean).join(' · ') || '—';

// Slide 294 — the Date column carries a source badge above the timestamp.
// Rows are school-raised today; the ML side will write source: 'suggested' for
// rows it proposes from assessment gaps, and this flips automatically.
const isSuggested = (r) => r.source === 'suggested';
const SOURCE_CHIP_REQUESTED = {
    backgroundColor: '#2563EB', color: '#fff', fontWeight: 700, fontSize: 11, height: 22,
};
const SOURCE_CHIP_SUGGESTED = {
    backgroundColor: '#EFF4FF', color: '#2563EB', fontWeight: 700, fontSize: 11, height: 22,
    border: '1px solid #C7D7FE',
};

const ProgramRequests = () => {
    const history = useHistory();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [deliveredCount, setDeliveredCount] = useState(0); // slide 271 "N Deliveries"
    const [pendingCount, setPendingCount] = useState(0);
    // slide 294 "Suggested Based on Needs" — written by the ML side, 0 until then.
    const [suggestedCount, setSuggestedCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [searchApplied, setSearchApplied] = useState('');
    const debounceRef = useRef(null);

    const [dialog, setDialog] = useState(null); // { mode: 'build'|'declined', row }
    const [declineReason, setDeclineReason] = useState('');      // selected preset or 'Other'
    const [declineOtherText, setDeclineOtherText] = useState(''); // free text when 'Other'
    const [declineError, setDeclineError] = useState('');
    const [acting, setActing] = useState(false);
    const [publishingId, setPublishingId] = useState(null);
    // Post-publish "link to school" prompt — { row } while open, null when closed.
    const [linkDialog, setLinkDialog] = useState(null);
    const [linking, setLinking] = useState(false);

    /**
     * Publish the program built for this request, straight from the table.
     * Publishing is what delivers it: the API marks the request Delivered and
     * emails the school. Uses the same publish endpoint as the program pages.
     */
    const publishForRequest = async (r) => {
        if (!r?.program_code || publishingId) return;
        setPublishingId(r._id);
        try {
            const res = await publishLearningProgramService(r.program_code);
            if (res?.data) {
                await updateProgramRequestStatusService({
                    program_code: r.program_code,
                    status: WORKSPACE_REQUEST_STATUS.BUILT,
                }).catch(() => {});
                toast(TOAST_TYPES.SUCCESS, 'Published',
                    `Program delivered to ${r.school_name || r.org_code}. They have been notified by email.`);
                await load();
                // Publishing alone does not make the program visible to the school —
                // it must also be linked to their org. Prompt for that right away so
                // the delivery is actually usable.
                setLinkDialog({ row: r });
            }
        } catch {
            /* invokeApi surfaces the backend message (e.g. "Complete all sessions…") */
        } finally {
            setPublishingId(null);
        }
    };

    /**
     * Link the published program to the requesting school's org, which is what
     * makes it appear in their workspace and enables their "View Program".
     *
     * Uses the SAME endpoint the Client List uses for "Link Learning Program"
     * (link_learning_program) — that is what CREATES the org↔module link.
     * set_module_visibility only changes the scope of a link that already
     * exists, so it 404s ("not linked to org") when used to link.
     */
    const linkToSchool = async () => {
        const r = linkDialog?.row;
        if (!r?.program_code || !r?.org_code || linking) return;
        setLinking(true);
        try {
            const res = await linkLearningProgramService(r.org_code, [r.program_code]);
            if (res?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Linked',
                    `${r.school_name || r.org_code} can now access this program.`);
                setLinkDialog(null);
                await load();
            }
        } catch {
            /* invokeApi surfaces the backend error (e.g. no license seats) */
        } finally {
            setLinking(false);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listProgramRequestsService({ search: searchApplied, page: page + 1, limit: rowsPerPage });
            const data = (res && res.data) || {};
            setRows(Array.isArray(data.requests) ? data.requests : []);
            setTotal(data.total || 0);
            setDeliveredCount(Number(data.delivered_total) || 0);
            setPendingCount(Number(data.pending_total) || 0);
            setSuggestedCount(Number(data.suggested_total) || 0);
        } catch { setRows([]); setTotal(0); setDeliveredCount(0); setPendingCount(0); setSuggestedCount(0); }
        setLoading(false);
    }, [searchApplied, page, rowsPerPage]);

    useEffect(() => { load(); }, [load]);

    // Debounced server-side search — resets to page 1.
    const onSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { setPage(0); setSearchApplied(val.trim()); }, 400);
    };

    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

    const openBuild = (row) => setDialog({ mode: 'build', row });
    const openDecline = (row) => {
        setDeclineReason(''); setDeclineOtherText(''); setDeclineError('');
        setDialog({ mode: 'declined', row });
    };

    /**
     * Navigate into the creator to build `r`, stashing the request so publishing
     * marks it delivered + emails the school (school_name feeds the slide-155
     * "linked to (school)" confirmation).
     */
    const goBuild = (r) => {
        try {
            sessionStorage.setItem(PENDING_REQUEST_KEY, JSON.stringify({
                id: r._id,
                school_name: r.school_name || r.org_code || '',
                // The creator links the published program straight to this school
                // from its publish dialog, so it needs the org — without it the
                // owner would be sent to the client list to hunt for the school.
                org_code: r.org_code || '',
                // Carried on RESUME so publishing binds to the same program that
                // was already started for this request.
                program_code: r.program_code || '',
            }));
        } catch { /* ignore */ }
        // Mark the request as being worked on so the row stops offering "Build
        // Program" while a build is underway. Best effort — never block the build.
        if (Number(r.status) === WORKSPACE_REQUEST_STATUS.PENDING) {
            updateProgramRequestStatusService({
                request_id: r._id,
                status: WORKSPACE_REQUEST_STATUS.IN_PROGRESS,
            }).catch(() => {});
        }
        setDialog(null);
        // Pass the request to the creator using the WIZARD field names
        // (DEFAULT_WIZARD_VALUES) so it can pre-fill the build wizard directly.
        history.push('/dq_pro/program/creation/preview', {
            programRequest: {
                request_id:                 r._id,
                // The program already started for THIS request (empty on a first
                // build). The creator restores this request's own chat from it
                // instead of the workspace-wide active chat, so two requests
                // never share one conversation.
                programCode:                r.program_code || '',
                Theme:                      r.topic || '',
                NumSessions:                r.num_sessions || null,
                SessionDuration:            r.session_duration_mins || null,
                SubjectContextTopic:        r.school_subject || '',
                CulturalSensitivityDetails: r.local_sensitivities || '',
                grade_level:                r.grade_level || '',
                AdditionalInfo:             r.additional_info || '',
            },
        });
    };

    /**
     * "Build" pressed on the slide-271 confirmation.
     *
     * Reuses the platform's existing in-progress-program guard
     * (getActiveChatInsiderProgramService) — the same check DQProCreate does —
     * so the owner is never dropped into a half-finished program by surprise.
     * If one is active we ask Continue vs Start Over first.
     */
    const confirmBuild = async () => {
        const r = dialog.row;
        setActing(true);
        try {
            const res = await getActiveChatInsiderProgramService();
            const activeProgramId = res?.data?.program_id;
            // Only warn when the in-progress program belongs to a DIFFERENT piece
            // of work. If it is this request's own program, resuming it is exactly
            // what "Build" should do — asking Continue/Start Over there would make
            // the owner choose between a request and itself.
            if (activeProgramId && activeProgramId !== r.program_code) {
                setActing(false);
                setDialog({ mode: 'existing', row: r, programId: activeProgramId });
                return;
            }
        } catch {
            /* guard is best-effort — fall through to a normal build */
        }
        setActing(false);
        goBuild(r);
    };

    /** Start Over — clear the in-progress program, then build the request. */
    const confirmStartOver = async () => {
        const { row, programId } = dialog;
        setActing(true);
        try {
            if (programId) await clearChatStateInsiderService(programId);
        } catch { /* best effort — still proceed */ }
        setActing(false);
        goBuild(row);
    };

    /**
     * Continue — resume the existing program instead. We deliberately do NOT
     * stash the request here: that program was not built for this request, so
     * publishing it must not mark the request delivered. It stays Pending.
     */
    const continueExisting = () => {
        try { sessionStorage.removeItem(PENDING_REQUEST_KEY); } catch { /* ignore */ }
        setDialog(null);
        history.push('/dq_pro/program/creation/preview');
    };

    const confirmDecline = async () => {
        // Slide 9 design note: "A reason is required" — the school always gets a why.
        const finalReason = declineReason === DECLINE_OTHER
            ? String(declineOtherText || '').trim()
            : declineReason;
        if (!finalReason) {
            setDeclineError(declineReason === DECLINE_OTHER
                ? 'Please type a reason.'
                : 'Please select a reason — it is shared with the school.');
            return;
        }
        setActing(true);
        try {
            const res = await updateProgramRequestStatusService({ request_id: dialog.row._id, status: WORKSPACE_REQUEST_STATUS.DECLINED, decline_reason: finalReason });
            if (res && res.data) {
                toast(TOAST_TYPES.SUCCESS, 'Requests', 'Request declined.');
                setDialog(null);
                await load();
            }
        } catch { /* toasted upstream */ } finally { setActing(false); }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Partner Workspace deck 13Aug2026, slide 22. Retitled from "Learning
                Program Requests from Schools": the list is specified to carry
                partner-side opportunities generated from assessment data as well as
                what a school asked for. */}
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: NAVY }}>Program Opportunities</Typography>
            <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #6A7282)', mt: 0.5, mb: 2.5 }}>
                The following opportunities are available for your schools based on their
                requests and assessment insights.
            </Typography>

            {/* Stat cards — Requests / Pending / Suggested Based on Needs /
                Delivered (slide 294). "Suggested" is produced by the ML side from
                assessment gaps; until it writes any, the card shows a dash rather
                than a misleading zero. */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {[
                    { label: 'Requests',               value: total,          color: '#052F44' },
                    { label: 'Awaiting your response', value: pendingCount,   color: '#EFA020' },
                    {
                        label: 'Ready to suggest',
                        value: suggestedCount > 0 ? suggestedCount : '—',
                        color: '#2563EB',
                    },
                    { label: 'Delivered',              value: deliveredCount, color: '#1B6B55' },
                ].map((s) => (
                    <Grid item xs={12} sm={6} md={3} key={s.label}>
                        <Box sx={{
                            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                            backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            overflow: 'hidden', height: '100%',
                        }}>
                            <Box sx={{ backgroundColor: s.color, height: '6px' }} />
                            <Box sx={{ p: 2.5 }}>
                                <Typography sx={{
                                    fontSize: '11px', fontWeight: 700, color: 'var(--brand-text-muted, #6A7282)',
                                    letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5,
                                }}>
                                    {s.label}
                                </Typography>
                                <Typography sx={{ fontSize: '34px', fontWeight: 800, color: NAVY, lineHeight: 1 }}>
                                    {s.value}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ mb: 2, maxWidth: 380 }}>
                <TextField fullWidth size="small" value={search} onChange={onSearchChange}
                    placeholder="Search by school or topic…"
                    sx={{ backgroundColor: 'var(--brand-surface, #fff)' }} />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : rows.length === 0 ? (
                <Box sx={{ border: '1px dashed rgba(241,90,36,0.3)', borderRadius: '16px', p: '60px 20px', textAlign: 'center', backgroundColor: 'var(--brand-surface, #fff)' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: NAVY, mb: 1 }}>
                        {searchApplied ? 'No requests match your search' : 'No requests yet'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #6A7282)' }}>
                        {searchApplied ? 'Try a different school or topic.' : 'When a school requests a learning program, it will appear here.'}
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', backgroundColor: 'var(--brand-surface, #fff)' }}>
                        <Table sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#052F44' }}>
                                    {/* Slide 15 names the columns: Request date, Request
                                        type, School / Network name, Quantity, Status,
                                        Primary action. "Area"/"Code"/"Duration"/"Group"
                                        are kept — they carry the request detail this page
                                        already has and slide 22's rows show the same.

                                        Slide 15 lists Status and Primary action as separate
                                        columns. They stay in ONE cell here: the body renders the
                                        status chip and its button together inside a single
                                        conditional per status, and splitting that would mean
                                        rewriting every branch for no visible gain. The header
                                        names both so the column still reads as the slide does. */}
                                    {['Request date', 'School / Network', 'Area', 'Code', 'Duration', 'Group', 'Status / Primary action'].map((h) => (
                                        <TableCell key={h} sx={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => {
                                    const st = STATUS_CHIP[r.status] || STATUS_CHIP[WORKSPACE_REQUEST_STATUS.PENDING];
                                    const isPending = Number(r.status) === WORKSPACE_REQUEST_STATUS.PENDING;
                                    return (
                                        <TableRow key={r._id} hover>
                                            {/* Slide 294 — source badge sits ABOVE the date:
                                                "Requested" (school raised it) or "Suggested"
                                                (proposed from assessment gaps by the ML side). */}
                                            <TableCell sx={{ fontSize: 13 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                                    <Chip
                                                        label={isSuggested(r) ? 'Suggested' : 'Requested'}
                                                        size="small"
                                                        sx={isSuggested(r) ? SOURCE_CHIP_SUGGESTED : SOURCE_CHIP_REQUESTED}
                                                    />
                                                    <Typography sx={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6B7280)', whiteSpace: 'nowrap' }}>
                                                        {fmtDate(r.createdAt)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{r.school_name || r.org_code}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.topic || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{(r.associated_dq_codes || []).join(', ') || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{fmtDuration(r)}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{fmtGroup(r)}</TableCell>
                                            <TableCell>
                                                {isPending ? (
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        {/* Both actions lock while one is starting, so a
                                                            double-click can't fire two builds or race a decline. */}
                                                        <Tooltip arrow title="Build a learning program for this school from their request. You can review and edit it before publishing — the school is notified automatically once you publish.">
                                                            <span>
                                                                <Button size="small" variant="contained"
                                                                    disabled={acting || !!publishingId}
                                                                    onClick={() => openBuild(r)}
                                                                    sx={primaryBtnSx}>
                                                                    Build Program
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                        {/* Build Program already explains itself on hover; Decline
                                                            did not, so the pair looked inconsistent. Span wrapper
                                                            because MUI drops pointer events on a disabled button,
                                                            which would swallow the tooltip. */}
                                                        <Tooltip arrow title="Decline this request. You'll add a reason the school will see.">
                                                            <span>
                                                                <Button size="small" variant="outlined"
                                                                    disabled={acting || !!publishingId}
                                                                    onClick={() => openDecline(r)}
                                                                    sx={{
                                                                        textTransform: 'none', whiteSpace: 'nowrap',
                                                                        color: 'var(--brand-text-muted, #6B7280)',
                                                                        borderColor: 'var(--brand-border, #D1D5DB)',
                                                                        '&:hover': { borderColor: 'var(--brand-text-muted, #9CA3AF)', backgroundColor: 'rgba(0,0,0,0.03)' },
                                                                    }}>
                                                                    Decline
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                ) : Number(r.status) === WORKSPACE_REQUEST_STATUS.IN_PROGRESS ? (
                                                    /* Build underway. The program itself tells us the real stage:
                                                       once content generation is complete it can be published
                                                       straight from here, which delivers it to the school. */
                                                    r.program_is_ready ? (
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <Chip label="Completed" size="small" sx={{ backgroundColor: '#1B6B55', color: '#fff', fontWeight: 700 }} />
                                                            <Tooltip arrow title="Publish this program. It is delivered to the school and they are notified by email.">
                                                                <Button size="small" variant="contained"
                                                                    disabled={publishingId === r._id}
                                                                    onClick={() => publishForRequest(r)}
                                                                    sx={primaryBtnSx}>
                                                                    {publishingId === r._id ? 'Publishing…' : 'Publish'}
                                                                </Button>
                                                            </Tooltip>
                                                        </Box>
                                                    ) : (
                                                        /* No program bound to this request yet — the owner opened the
                                                           builder but never generated anything, so nothing is actually
                                                           running. Saying "Building…" for days is simply untrue, so
                                                           label it honestly and invite them to start. */
                                                        !String(r.program_code || '').trim() ? (
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                <Tooltip arrow title="This request was opened in the builder, but no program has been generated for it yet.">
                                                                    <Chip label="Not started" size="small"
                                                                        sx={{ backgroundColor: '#9CA3AF', color: '#fff', fontWeight: 700 }} />
                                                                </Tooltip>
                                                                <Button size="small" variant="outlined" onClick={() => goBuild(r)}
                                                                    sx={{
                                                                        textTransform: 'none', whiteSpace: 'nowrap',
                                                                        color: ORANGE, borderColor: ORANGE,
                                                                        '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
                                                                    }}>
                                                                    Start Building
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                <Chip label={st.label} size="small" sx={{ backgroundColor: st.bg, color: '#fff', fontWeight: 700 }} />
                                                                <Tooltip arrow title="Resume building this program. It can be published once all sessions are generated.">
                                                                    <Button size="small" variant="outlined" onClick={() => goBuild(r)}
                                                                        sx={{
                                                                            textTransform: 'none', whiteSpace: 'nowrap',
                                                                            color: ORANGE, borderColor: ORANGE,
                                                                            '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
                                                                        }}>
                                                                        Continue Building
                                                                    </Button>
                                                                </Tooltip>
                                                            </Box>
                                                        )
                                                    )
                                                ) : (
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <Tooltip title={Number(r.status) === WORKSPACE_REQUEST_STATUS.DECLINED && r.decline_reason ? r.decline_reason : ''} arrow>
                                                            <Chip label={st.label} size="small" sx={{ backgroundColor: st.bg, color: '#fff', fontWeight: 700 }} />
                                                        </Tooltip>
                                                        {/* Delivered but not yet visible to the school — offer the
                                                            link step here too (e.g. "Later" was chosen after publish). */}
                                                        {Number(r.status) === WORKSPACE_REQUEST_STATUS.BUILT
                                                            && r.program_code && r.program_linked === false && (
                                                            <Tooltip arrow title="This program is not linked to the school yet, so they cannot open it.">
                                                                <Button size="small" variant="outlined"
                                                                    onClick={() => setLinkDialog({ row: r })}
                                                                    sx={{
                                                                        textTransform: 'none', whiteSpace: 'nowrap',
                                                                        color: ORANGE, borderColor: ORANGE,
                                                                        '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
                                                                    }}>
                                                                    Link to School
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Server-side pagination — matches the Manage Classes pager. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6B7280)' }}>Rows per page:</Typography>
                            <TextField select size="small" value={rowsPerPage}
                                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                                sx={{ width: 76, backgroundColor: 'var(--brand-surface, #fff)' }}>
                                {PAGE_SIZE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6B7280)' }}>{`Page ${page + 1} of ${totalPages}`}</Typography>
                            <Button size="small" variant="outlined" sx={navBtnSx} disabled={loading || page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
                            <Button size="small" variant="outlined" sx={navBtnSx} disabled={loading || page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                        </Box>
                    </Box>
                </>
            )}

            {/* Build (slide 349) / Decline confirmation. Rounded Paper to match the
                app's other confirmation dialogs (e.g. "Existing Program Found"). */}
            <Dialog open={!!dialog} onClose={() => !acting && setDialog(null)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ fontWeight: 800, color: NAVY, textAlign: dialog?.mode === 'declined' ? 'left' : 'center' }}>
                    {dialog?.mode === 'declined' ? 'Decline Request'
                        : dialog?.mode === 'existing' ? 'Existing Program Found'
                        : 'Confirm Build Request from School'}
                </DialogTitle>
                <DialogContent>
                    {dialog?.mode === 'existing' ? (
                        /* Mirrors the platform's existing in-progress guard so the
                           owner can resume or discard before building the request. */
                        <>
                            <Typography sx={{ fontSize: 15, color: 'var(--brand-text, #374151)', lineHeight: 1.6 }}>
                                You already have a program in progress.<br />
                                Would you like to <strong>continue</strong> where you left off, or{' '}
                                <strong>start over</strong> and build{' '}
                                <strong>{dialog?.row?.topic || 'this request'}</strong> for{' '}
                                <strong>{dialog?.row?.school_name || dialog?.row?.org_code}</strong>?
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6A7282)', mt: 2 }}>
                                Continuing resumes your existing program — this request stays Pending.
                            </Typography>
                        </>
                    ) : dialog?.mode !== 'declined' ? (
                        /* Centered intro, a clean requirements panel (label ｜ value
                           rows), then the "proceed?" prompt — matches the app's info
                           panels rather than raw bullet lists. */
                        <>
                            <Typography sx={{ fontSize: 15, mb: 2.5, textAlign: 'center', color: 'var(--brand-text, #374151)' }}>
                                We will build a learning program for{' '}
                                <Box component="span" sx={{ fontWeight: 700, color: NAVY }}>{dialog?.row?.school_name || dialog?.row?.org_code}</Box>{' '}
                                with the following requirements:
                            </Typography>

                            <Box sx={{
                                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                                backgroundColor: 'var(--brand-surface-subtle, #F9FAFB)', overflow: 'hidden',
                            }}>
                                {[
                                    ['Topic', dialog?.row?.topic || '—'],
                                    ['DQ codes', (dialog?.row?.associated_dq_codes || []).join(', ') || '—'],
                                    ['Sessions', dialog?.row?.num_sessions
                                        ? `${dialog.row.num_sessions} × ${dialog?.row?.session_duration_mins || '?'} minutes`
                                        : '—'],
                                    ...(dialog?.row?.grade_level ? [['Group', dialog.row.grade_level]] : []),
                                    // Where this build came from. Every row on this page is a
                                    // school-submitted request, so it is always the school —
                                    // the field exists to distinguish these from programs the
                                    // owner starts themselves.
                                    ['Source', 'Requested by the school'],
                                    /*
                                     * Estimated Credit Cost — slide 22, which prints the rule
                                     * beside the figure: "30credit x # of session".
                                     *
                                     * Only when the request carries a session count. Without one
                                     * there is no multiplier, and a "0 credits" row would read as
                                     * free rather than as unknown.
                                     */
                                    ...(Number(dialog?.row?.num_sessions) > 0
                                        ? [[`Estimated Credit Cost`, `${CREDITS_PER_SESSION * Number(dialog.row.num_sessions)} credits`]]
                                        : []),
                                    // Delivery Requirements the school submitted (slide 245).
                                    // Optional on their form, so shown only when provided.
                                    ...(String(dialog?.row?.local_sensitivities || '').trim()
                                        ? [['Local Sensitivities', dialog.row.local_sensitivities]] : []),
                                    ...(String(dialog?.row?.school_subject || '').trim()
                                        ? [['Connected Subject', dialog.row.school_subject]] : []),
                                ].map(([label, value], i, arr) => (
                                    <Box key={label} sx={{
                                        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2,
                                        px: 2.5, py: 1.5,
                                        borderBottom: i < arr.length - 1 ? '1px solid var(--brand-border, #EEF0F2)' : 'none',
                                    }}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'var(--brand-text-muted, #6B7280)' }}>{label}</Typography>
                                        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: NAVY, textAlign: 'right' }}>{value}</Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: NAVY, mt: 2.5, textAlign: 'center' }}>
                                Do you wish to proceed?
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6A7282)', mt: 0.75, textAlign: 'center', lineHeight: 1.5 }}>
                                You can edit the topics, # of sessions, and session duration if you need to change it in the next screen.
                            </Typography>
                        </>
                    ) : (
                        <>
                            {/* Slide 9 — declining asks WHY; the reason is shared with the school. */}
                            <Typography sx={{ fontSize: 14, mb: 0.5 }}>
                                Decline <strong>{dialog?.row?.topic || 'this request'}</strong> from <strong>{dialog?.row?.school_name || dialog?.row?.org_code}</strong>
                                {dialog?.row?.grade_level ? ` · ${dialog.row.grade_level}` : ''}.
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6A7282)', mb: 1.5 }}>
                                Reason (shared with the school) — required.
                            </Typography>
                            <RadioGroup value={declineReason}
                                onChange={(e) => { setDeclineReason(e.target.value); setDeclineError(''); }}>
                                {DECLINE_REASONS.map((r) => (
                                    <FormControlLabel key={r} value={r} control={<Radio size="small" />}
                                        label={<Typography sx={{ fontSize: 13.5 }}>{r}</Typography>} />
                                ))}
                                <FormControlLabel value={DECLINE_OTHER} control={<Radio size="small" />}
                                    label={<Typography sx={{ fontSize: 13.5 }}>{DECLINE_OTHER}</Typography>} />
                            </RadioGroup>
                            {declineReason === DECLINE_OTHER && (
                                <TextField fullWidth multiline minRows={2} size="small" sx={{ mt: 1 }}
                                    placeholder="Type your reason…" value={declineOtherText}
                                    onChange={(e) => { const v = e.target.value; setDeclineOtherText(v); setDeclineError(''); }} />
                            )}
                            {declineError ? (
                                <Typography sx={{ fontSize: 12.5, color: '#DC2626', mt: 1 }}>{declineError}</Typography>
                            ) : null}
                            <Typography sx={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6A7282)', mt: 1.5 }}>
                                The request is kept as latent demand for the next reassessment cycle — it is not deleted.
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {dialog?.mode === 'existing' ? (
                        <>
                            <Button onClick={() => setDialog(null)} disabled={acting}
                                sx={{ textTransform: 'none', color: 'var(--brand-text-muted, #6B7280)' }}>
                                Cancel
                            </Button>
                            <Button onClick={confirmStartOver} disabled={acting} variant="outlined"
                                sx={{ textTransform: 'none', color: 'var(--brand-text, #111)', borderColor: 'var(--brand-text, #111)' }}>
                                {acting ? 'Working…' : 'Start Over'}
                            </Button>
                            <Button onClick={continueExisting} disabled={acting} variant="contained"
                                sx={dialogPrimarySx}>
                                Continue
                            </Button>
                        </>
                    ) : dialog?.mode === 'declined' ? (
                        <>
                            <Button onClick={() => setDialog(null)} disabled={acting} sx={{ textTransform: 'none', color: 'var(--brand-text-muted, #6B7280)' }}>
                                Keep request
                            </Button>
                            <Button onClick={confirmDecline} disabled={acting} variant="contained"
                                sx={dialogPrimarySx}>
                                {acting ? 'Working…' : 'Decline'}
                            </Button>
                        </>
                    ) : (
                        /* Slide 349 — Cancel + Build, centered. Pill buttons to
                           match the app's other confirmation dialogs. */
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, width: '100%', pb: 1 }}>
                            <Button onClick={() => setDialog(null)} disabled={acting} variant="outlined"
                                sx={dialogSecondarySx}>
                                Cancel
                            </Button>
                            <Button onClick={confirmBuild} disabled={acting} variant="contained"
                                sx={dialogPrimarySx}>
                                {acting ? 'Working…' : 'Build & deliver'}
                            </Button>
                        </Box>
                    )}
                </DialogActions>
            </Dialog>

            {/* Post-publish: link the program to the requesting school so it
                actually appears in their workspace (publish alone doesn't). */}
            <Dialog open={!!linkDialog} onClose={() => !linking && setLinkDialog(null)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY, mb: 1.5 }}>
                        Program published
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'var(--brand-text, #555)', mb: 3 }}>
                        Link it to <strong>{linkDialog?.row?.school_name || linkDialog?.row?.org_code}</strong> so they
                        can open it from their workspace.
                    </Typography>
                    {/* Compact, side by side — these are a small follow-up choice,
                        not the page's main action, so they don't run full width. */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" onClick={() => setLinkDialog(null)} disabled={linking}
                            sx={{ ...dialogSecondarySx, px: 2, fontSize: 13 }}>
                            Later
                        </Button>
                        <Button size="small" variant="contained" onClick={linkToSchool} disabled={linking}
                            sx={{ ...dialogPrimarySx, px: 2, fontSize: 13, whiteSpace: 'nowrap' }}>
                            {linking ? 'Linking…' : 'Link Program to School'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ProgramRequests;
